/**
 * @file User-friendly upload error messages from Uppy/API errors.
 */

export type UploadErrorLike = {
  message?: string;
  name?: string;
  status?: number;
  data?: unknown;
  request?: XMLHttpRequest;
};

/** Third argument to Uppy `upload-error`: typed response or raw XHR from xhr-upload. */
export type UppyUploadErrorResponse =
  | { status?: number; body?: unknown }
  | XMLHttpRequest
  | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const formatValidationDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') {
    return detail;
  }

  if (!Array.isArray(detail)) {
    return undefined;
  }

  const messages = detail
    .map((item) => {
      if (!isRecord(item)) {
        return undefined;
      }

      if (typeof item.msg === 'string') {
        return item.msg;
      }

      return undefined;
    })
    .filter((message): message is string => Boolean(message));

  return messages.length > 0 ? messages.join('. ') : undefined;
};

const extractApiMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }

  if (!isRecord(data)) {
    return undefined;
  }

  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    return data.message;
  }

  const detailMessage = formatValidationDetail(data.detail);
  if (detailMessage) {
    return detailMessage;
  }

  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return data.error;
  }

  return undefined;
};

const getStatusFallback = (status?: number): string | undefined => {
  switch (status) {
    case 400:
      return 'Upload failed: invalid request.';
    case 403:
      return 'Upload session expired. Please try again.';
    case 422:
      return 'Upload failed: invalid file or request.';
    case 500:
      return 'The server encountered an error. Please try again.';
    default:
      return undefined;
  }
};

const isCorsError = (error: UploadErrorLike): boolean =>
  error.name === 'CORS' ||
  (typeof error.message === 'string' &&
    error.message.toLowerCase().includes('cors'));

const parseXhrResponseBody = (xhr: XMLHttpRequest): unknown => {
  if (xhr.responseType === 'json' && xhr.response != null) {
    return xhr.response;
  }

  const text = xhr.responseText;
  if (typeof text !== 'string' || text.trim().length === 0) {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const isXmlHttpRequest = (value: unknown): value is XMLHttpRequest =>
  typeof value === 'object' &&
  value != null &&
  ('responseText' in value || 'response' in value);

const getUploadErrorStatus = (
  error: UploadErrorLike,
  response?: UppyUploadErrorResponse
): number | undefined => {
  if (typeof error.status === 'number') {
    return error.status;
  }

  if (response == null) {
    return undefined;
  }

  if (typeof response.status === 'number') {
    return response.status;
  }

  return undefined;
};

/**
 * Resolves API payload for upload errors.
 * xhr-upload usually puts the server body on the third event arg (XHR), not `error.data`.
 */
export const resolveUploadErrorData = (
  error: UploadErrorLike,
  response?: UppyUploadErrorResponse
): unknown => {
  if (error.data != null) {
    return error.data;
  }

  if (
    response != null &&
    typeof response === 'object' &&
    'body' in response &&
    response.body != null
  ) {
    return response.body;
  }

  const xhr = isXmlHttpRequest(response)
    ? response
    : isXmlHttpRequest(error.request)
      ? error.request
      : undefined;

  if (xhr) {
    return parseXhrResponseBody(xhr);
  }

  return undefined;
};

/**
 * Formats an Uppy `upload-error` event into user-facing copy.
 */
export const formatUppyUploadError = (
  error: UploadErrorLike,
  response?: UppyUploadErrorResponse
): string =>
  formatUploadError({
    message: error.message,
    name: error.name,
    status: getUploadErrorStatus(error, response),
    data: resolveUploadErrorData(error, response),
  });

/**
 * Converts a raw Uppy or API error into user-facing copy.
 * Prefers API response body messages over generic HTTP status text.
 */
export const formatUploadError = (error: UploadErrorLike): string => {
  if (isCorsError(error)) {
    return 'Upload failed: network or CORS error. Please try again.';
  }

  const apiMessage = extractApiMessage(error.data);
  if (apiMessage) {
    return apiMessage;
  }

  const statusFallback = getStatusFallback(error.status);
  if (statusFallback) {
    return statusFallback;
  }

  if (typeof error.message === 'string' && error.message.trim().length > 0) {
    const httpPrefix = /^HTTP \d{3}:/;
    if (httpPrefix.test(error.message)) {
      return 'Upload failed. Please try again.';
    }

    return error.message;
  }

  return 'Upload failed';
};
